/**
 * Document asset resolution (§4.15).
 *
 * A `.md` doc may reference images by a workspace-relative path
 * (`![](./images/x.svg)`). The document surface can't let the browser fetch
 * such a path — there is no real server behind the in-memory workspace — so we
 * resolve the path against the doc's own location, look the file up in the
 * workspace {@link Assets} channel (raw bytes, keyed by POSIX path), and hand
 * the bytes back for the surface to wrap in a `blob:` URL. External
 * (`http(s):`/`data:`/`blob:`) srcs are left for the browser.
 */

import { type Assets, assetMime } from "../workspaceAssets.js";

/** Resolve a relative image `src` (against the doc's path) to the bytes of a
 *  workspace asset, so a relative `<img>` can be shown via a `blob:` URL.
 *  Returns undefined when the path isn't a workspace asset. */
export type AssetResolver = (
  src: string,
  docPath: string,
) => { bytes: Uint8Array; mime: string } | undefined;

/** Resolve a doc-relative (or workspace-absolute) `src` to a normalized VFS
 *  path. Returns undefined for external schemes the browser handles itself. */
export function resolveWorkspacePath(docPath: string, src: string): string | undefined {
  if (/^(https?:|data:|blob:|archlang:)/i.test(src)) return undefined;
  const clean = src.replace(/[?#].*$/, "").trim();
  if (!clean) return undefined;
  const dir = docPath.slice(0, docPath.lastIndexOf("/"));
  const joined = clean.startsWith("/") ? clean : `${dir}/${clean}`;
  return normalizePosix(joined);
}

/** Collapse `.`/`..` segments to a single rooted POSIX path. */
function normalizePosix(p: string): string {
  const segs: string[] = [];
  for (const seg of p.split("/")) {
    if (seg === "" || seg === ".") continue;
    if (seg === "..") segs.pop();
    else segs.push(seg);
  }
  return "/" + segs.join("/");
}

/** Build an {@link AssetResolver} over a live view of the workspace assets. */
export function makeAssetResolver(getAssets: () => Assets): AssetResolver {
  return (src, docPath) => {
    const path = resolveWorkspacePath(docPath, src);
    if (path === undefined) return undefined;
    const bytes = getAssets().get(path);
    if (bytes === undefined) return undefined;
    return { bytes, mime: assetMime(path) };
  };
}
